import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, Check, Calendar, CreditCard, Layers, FastForward } from "lucide-react";
import { Layout } from "../components/Layout";
import { StatusBadge } from "../components/StarterBuild";
import { DeliveryDashboard } from "../components/DeliveryDashboard";
import { fetchStarterBuild, updateStarterBuild } from "../lib/api";
import { useAuth } from "../hooks/useAuth";

const STEPS = [
  { key: "reviewing", label: "Reviewing", desc: "We review your request and requirements." },
  { key: "designing", label: "Designing", desc: "Our team designs your app concept & structure." },
  { key: "preview-ready", label: "Preview Ready", desc: "Your interactive live preview is ready to explore." },
  { key: "completed", label: "Completed", desc: "Final build delivered and ready to launch." },
];

const order = STEPS.map((s) => s.key);

export default function CustomAppDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, startLogin } = useAuth();
  const [build, setBuild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [advancing, setAdvancing] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    fetchStarterBuild(id)
      .then(setBuild)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id, user, authLoading]);

  const activeIndex = build ? order.indexOf(build.status) : -1;
  const isCompleted = build?.status === "completed";

  const advance = async () => {
    const next = order[activeIndex + 1];
    if (!next) return;
    setAdvancing(true);
    try {
      const updated = await updateStarterBuild(build.id, next);
      setBuild(updated);
      toast.success(`Status updated to ${STEPS.find((s) => s.key === next).label}`);
    } catch {
      toast.error("Could not update status");
    } finally {
      setAdvancing(false);
    }
  };

  return (
    <Layout>
      <section className="max-w-4xl mx-auto px-6 lg:px-8 py-12">
        <button
          onClick={() => navigate("/profile")}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#687280] hover:text-[#0F0F0F] transition-colors mb-8"
          data-testid="build-back-to-profile"
        >
          <ArrowLeft size={15} /> Back to profile
        </button>

        {!user && !authLoading ? (
          <div className="bg-[#F7F7F7] rounded-2xl p-10 text-center" data-testid="build-detail-signin">
            <p className="font-display text-xl font-semibold text-[#0F0F0F]">Sign in to view your build</p>
            <button
              onClick={startLogin}
              className="mt-5 inline-flex items-center gap-2 bg-[#0F0F0F] text-white rounded-full px-6 py-3 text-sm font-semibold hover:bg-[#1c1c1c] transition-colors"
              data-testid="build-detail-signin-btn"
            >
              Continue with Google
            </button>
          </div>
        ) : loading ? (
          <p className="text-[#687280]" data-testid="build-detail-loading">Loading…</p>
        ) : error || !build ? (
          <div className="text-center py-16" data-testid="build-detail-notfound">
            <p className="font-display text-xl font-semibold text-[#0F0F0F]">Build not found</p>
            <p className="text-[#687280] mt-1">This project doesn't exist or isn't yours.</p>
          </div>
        ) : (
          <div data-testid="build-detail-content">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div>
                <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-[#0F0F0F]">
                  {build.app_name}
                </h1>
                {build.industry && <p className="text-[#D4AF37] font-medium mt-1">{build.industry}</p>}
              </div>
              <StatusBadge status={build.status} />
            </div>
            {build.description && <p className="text-[#687280] mt-3 max-w-2xl">{build.description}</p>}

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-8">
              {[
                { icon: CreditCard, label: "Purchased", value: build.purchase_date },
                { icon: Calendar, label: "Est. completion", value: build.estimated_completion },
                { icon: Layers, label: "Platform", value: build.target_platform || "—" },
                { icon: Check, label: "Progress", value: `${build.progress}%` },
              ].map((m) => {
                const Icon = m.icon;
                return (
                  <div key={m.label} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
                    <div className="w-9 h-9 rounded-lg bg-[#F7F7F7] text-[#D4AF37] flex items-center justify-center mb-3">
                      <Icon size={17} />
                    </div>
                    <p className="text-xs text-[#687280]">{m.label}</p>
                    <p className="font-semibold text-[#0F0F0F] text-sm mt-0.5">{m.value}</p>
                  </div>
                );
              })}
            </div>

            {/* Simulate control (demo) */}
            {!isCompleted && (
              <div className="mt-6 flex items-center gap-3 rounded-2xl border border-dashed border-gray-200 bg-[#F7F7F7] px-5 py-4">
                <p className="text-sm text-[#687280] flex-1">
                  Demo control: simulate this project moving through the pipeline.
                </p>
                <button
                  onClick={advance}
                  disabled={advancing}
                  className="inline-flex items-center gap-2 bg-[#0F0F0F] text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#1c1c1c] transition-colors disabled:opacity-50"
                  data-testid="simulate-advance-btn"
                >
                  <FastForward size={15} />
                  {advancing ? "Updating…" : `Advance to ${STEPS[activeIndex + 1]?.label}`}
                </button>
              </div>
            )}

            {isCompleted ? (
              <div className="mt-10">
                <DeliveryDashboard build={build} />
              </div>
            ) : (
              <>
                {/* Progress timeline */}
                <div className="mt-10">
                  <h2 className="font-display text-xl font-bold text-[#0F0F0F] mb-5">Project status</h2>
                  <div className="space-y-1">
                    {STEPS.map((s, i) => {
                      const done = i < activeIndex;
                      const current = i === activeIndex;
                      return (
                        <div key={s.key} className="flex gap-4" data-testid={`timeline-step-${s.key}`}>
                          <div className="flex flex-col items-center">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                                done ? "bg-[#D4AF37] text-[#0F0F0F]" : current ? "bg-[#0F0F0F] text-white" : "bg-[#F1F1F0] text-[#A1A1AA]"
                              }`}
                            >
                              {done ? <Check size={15} /> : i + 1}
                            </div>
                            {i < STEPS.length - 1 && (
                              <div className={`w-0.5 flex-1 my-1 ${i < activeIndex ? "bg-[#D4AF37]" : "bg-[#F1F1F0]"}`} style={{ minHeight: 28 }} />
                            )}
                          </div>
                          <div className="pb-6">
                            <p className={`font-semibold ${current || done ? "text-[#0F0F0F]" : "text-[#A1A1AA]"}`}>{s.label}</p>
                            <p className="text-sm text-[#687280] mt-0.5">{s.desc}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Live preview (available once designing/preview-ready) */}
                {build.live_preview_url && activeIndex >= 1 && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-display text-xl font-bold text-[#0F0F0F]">Live preview</h2>
                      <button
                        onClick={() => window.open(build.live_preview_url, "_blank", "noopener,noreferrer")}
                        className="inline-flex items-center gap-2 text-sm font-semibold text-[#0F0F0F] hover:text-[#D4AF37] transition-colors"
                        data-testid="build-open-preview-btn"
                      >
                        Open in new tab <ExternalLink size={15} />
                      </button>
                    </div>
                    <div className="rounded-2xl border border-gray-100 overflow-hidden shadow-[0_16px_40px_rgb(0,0,0,0.08)] bg-white">
                      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 bg-[#F7F7F7]">
                        <span className="w-3 h-3 rounded-full bg-[#FF5F57]" />
                        <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
                        <span className="w-3 h-3 rounded-full bg-[#28C840]" />
                        <span className="ml-3 text-xs text-[#687280] truncate">{build.live_preview_url}</span>
                      </div>
                      <iframe
                        src={build.live_preview_url}
                        title={`${build.app_name} live preview`}
                        className="w-full h-[600px] bg-white"
                        sandbox="allow-scripts allow-forms allow-popups"
                        data-testid="build-preview-iframe"
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </section>
    </Layout>
  );
}
